import type { ChartDataset } from 'chart.js';
import type { TupleData } from './App';

const COLOR_MAP: Record<string, string> = {
    red: 'red',
    blue: 'blue',
    green: 'green',
    black: 'black',
};

export function latexColor(bgColor: unknown): string {
    if (typeof bgColor === 'string' && bgColor in COLOR_MAP) {
        return COLOR_MAP[bgColor];
    }
    return 'black';
}

export function generateLatex(datasets: ChartDataset<'scatter', TupleData[]>[]): string {
    const allPoints = datasets.flatMap(ds => ds.data);
    if (allPoints.length === 0) return '';

    const xs = allPoints.map(p => p.x);
    const ys = allPoints.map(p => p.y);
    const xMin = Math.min(...xs) - 1;
    const xMax = Math.max(...xs) + 1;
    const yMin = Math.min(...ys) - 1;
    const yMax = Math.max(...ys) + 1;

    const lines: string[] = [
        '\\documentclass{standalone}',
        '\\usepackage{spectralsequences}',
        '\\usepackage{xcolor}',
        '',
        '\\begin{document}',
        `\\begin{sseqpage}[ x range = {${xMin}}{${xMax}}, y range = {${yMin}}{${yMax}}, classes = {fill, inner sep = 0.35em} ]`,
    ];

    for (const ds of datasets) {
        const color = latexColor(ds.backgroundColor);
        lines.push(`    % Dataset: ${ds.label ?? 'Unnamed'}`);
        for (const point of ds.data) {
            const count = Math.max(point.dimension, 1);
            for (let i = 0; i < count; i++) {
                lines.push(`    \\class[${color}](${point.x}, ${point.y}) % basis: ${point.basis} (dim ${point.dimension})`);
            }
        }
    }

    lines.push('\\end{sseqpage}');
    lines.push('\\end{document}');
    lines.push('');

    return lines.join('\n');
}

export function getExportFilename(datasets: ChartDataset<'scatter', TupleData[]>[]): string {
    if (datasets.length === 0) return 'sseq.txt';

    const labels = datasets
        .map(ds => ds.label ?? 'Unnamed')
        .join('_')
        .replace(/[^a-zA-Z0-9_-]/g, '_');

    return `${labels}_sseq.txt`;
}

export function downloadLatex(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
